/**
 * localDevSessionBridge.ts
 *
 * LOCAL UX PREVIEW / DEV BRIDGE ONLY
 * ====================================
 * This module runs ONLY when the app is served from localhost / 127.0.0.1
 * (or Vite dev mode).  It must NEVER run on deployed Pages, production, or
 * staging domains.
 *
 * Purpose:
 *   When a developer opens the Setup Wizard locally and has only a mock
 *   Google session (mock_google_session / mock_google_user) but no real
 *   Worker session token or tenantId, clicking "ขั้นตอนถัดไป" at Step 2
 *   would normally show "ไม่พบเซสชั่นการเชื่อมต่อ (Session Token)".
 *   This bridge creates safe, obviously-fake local-dev credentials so the
 *   developer can reach Step 3, the Success screen, and the DormDashboard
 *   to test UX without touching production systems.
 *
 * Safety guarantees:
 *   - Only activates on localhost / 127.0.0.1 / ::1
 *   - All generated tokens are prefixed "local-dev-" — not accepted by any
 *     real backend.
 *   - No real production endpoints are called.
 *   - No real secrets, real emails, or payment data are stored beyond what
 *     the wizard already allows.
 *   - Raw PromptPay / accountName are NOT persisted (consistent with
 *     Prompt 52/53 draft safety — they are in-memory React state only).
 *   - Blob URLs are NOT persisted to localStorage.
 *   - Production auth guard (worker_session_token check) is untouched;
 *     this bridge only provides a fallback when that guard would block.
 *
 * Do not import this module from production entry paths outside of the
 * localhost guard inside App.tsx.
 */

// ─── Hostname guard ───────────────────────────────────────────────────────────

/**
 * Returns true ONLY when the app is running on a local dev host.
 * Vite's import.meta.env.DEV is also checked as a secondary signal.
 */
export function isLocalDevHost(): boolean {
  const host = window.location.hostname;
  const isLocalHost =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host === '0.0.0.0';
  // Vite sets import.meta.env.DEV = true in dev mode
  const isViteDev =
    typeof import.meta !== 'undefined' &&
    typeof (import.meta as any).env !== 'undefined' &&
    (import.meta as any).env.DEV === true;
  return isLocalHost || isViteDev;
}

// ─── Deterministic local tenant ID ───────────────────────────────────────────

/**
 * Derives a deterministic, obviously-fake tenantId from an email string.
 * Uses a simple FNV-1a-inspired hash — no crypto needed, collision risk is
 * irrelevant for local dev.  Result is always prefixed "local-dev-tenant-"
 * so it can never be confused with a real UUID.
 */
export function deriveLocalDevTenantId(email: string): string {
  let hash = 0x811c9dc5; // FNV offset basis (32-bit)
  for (let i = 0; i < email.length; i++) {
    hash ^= email.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0; // FNV prime, keep 32-bit unsigned
  }
  const hex = hash.toString(16).padStart(8, '0');
  return `local-dev-tenant-${hex}`;
}

// ─── Session token generator ──────────────────────────────────────────────────

/**
 * Returns a fresh obviously-fake session token for local dev use.
 * Prefixed "local-dev-session-" — will be rejected by any real backend.
 */
export function generateLocalDevSessionToken(): string {
  const rand = Math.random().toString(36).slice(2, 10);
  const ts = Date.now().toString(36);
  return `local-dev-session-${ts}-${rand}`;
}

// ─── Bridge result type ───────────────────────────────────────────────────────

export interface LocalDevSessionResult {
  /** Whether the bridge activated and created a session */
  activated: boolean;
  /** The obviously-fake session token (prefixed "local-dev-session-") */
  sessionToken: string;
  /** The deterministic fake tenantId (prefixed "local-dev-tenant-") */
  tenantId: string;
  /** Human-readable status for console or optional UI hint */
  statusMessage: string;
}

// ─── Main bridge function ─────────────────────────────────────────────────────

/**
 * Checks whether a local dev session bridge is needed and — if so — creates
 * safe local-dev credentials and stores them in localStorage in the same
 * keys that the real auth flow uses (worker_session_token, worker_tenant_id,
 * worker_has_tenant).
 *
 * Call this from App.tsx ONLY when:
 *   1. isLocalDevHost() is true
 *   2. There is a mock_google_user in localStorage
 *   3. There is NO real worker_session_token
 *
 * Returns { activated: false, ... } if the guard conditions are not met.
 * In that case the caller should proceed with the original (blocking) logic.
 *
 * @param currentUser  The parsed mock_google_user object (must have .email)
 */
export function activateLocalDevSessionBridge(
  currentUser: { email?: string; name?: string } | null
): LocalDevSessionResult {
  const notActivated: LocalDevSessionResult = {
    activated: false,
    sessionToken: '',
    tenantId: '',
    statusMessage: 'Bridge not activated: conditions not met',
  };

  // Hard guard: only localhost/dev
  if (!isLocalDevHost()) {
    return notActivated;
  }

  // Must have a mock user with an email
  const email =
    currentUser?.email ||
    (() => {
      try {
        const raw = localStorage.getItem('mock_google_user');
        if (raw) return JSON.parse(raw)?.email;
      } catch (_) {
        return undefined;
      }
    })();

  if (!email || typeof email !== 'string') {
    return { ...notActivated, statusMessage: 'Bridge not activated: no mock user email found' };
  }

  // Already have a real (or previously bridged) session token?
  const existingToken = localStorage.getItem('worker_session_token');
  if (existingToken) {
    // Re-use existing token — may be a previously bridged local-dev token
    const existingTenantId =
      localStorage.getItem('worker_tenant_id') || deriveLocalDevTenantId(email);
    return {
      activated: true,
      sessionToken: existingToken,
      tenantId: existingTenantId,
      statusMessage:
        '[LOCAL DEV BRIDGE] Re-using existing session token: ' + existingToken.slice(0, 28) + '...',
    };
  }

  // Create fresh local-dev credentials
  const sessionToken = generateLocalDevSessionToken();
  const tenantId = deriveLocalDevTenantId(email);

  // Persist in the same keys the real auth flow uses so the rest of App.tsx
  // can read them transparently.
  //
  // NOTE: These values are obviously fake (prefixed local-dev-*).
  //       They are stored ONLY to make the wizard flow readable across steps;
  //       they will be cleared on logout exactly like real session keys.
  localStorage.setItem('worker_session_token', sessionToken);
  localStorage.setItem('worker_tenant_id', tenantId);
  localStorage.setItem('worker_has_tenant', 'false'); // tenant not yet created
  localStorage.setItem('local_dev_bridge_active', 'true'); // sentinel for diagnostics

  const statusMessage =
    `[LOCAL DEV BRIDGE] Created local-dev session.\n` +
    `  sessionToken : ${sessionToken}\n` +
    `  tenantId     : ${tenantId}\n` +
    `  email source : ${email}\n` +
    `  hostname     : ${window.location.hostname}\n` +
    `IMPORTANT: These tokens are fake and accepted ONLY on localhost.\n` +
    `           All API calls will be skipped or mocked in local dev mode.`;

  console.info(statusMessage);

  return { activated: true, sessionToken, tenantId, statusMessage };
}

// ─── Mock API response shapes ─────────────────────────────────────────────────

/**
 * Returns a safe local-only mock response compatible with createTenant() shape.
 * localDevOnly marker ensures callers can distinguish from real API responses.
 */
export function mockLocalCreateTenantResponse(tenantId: string, dormName: string) {
  return {
    ok: true,
    localDevOnly: true,
    tenant: {
      id: tenantId,
      dormName: dormName || 'Local Dev Dorm',
      status: 'onboarding',
    },
  };
}

/**
 * Returns a safe local-only mock response compatible with updateTenantSettings() shape.
 */
export function mockLocalUpdateSettingsResponse(tenantId: string) {
  return {
    ok: true,
    localDevOnly: true,
    tenant: { id: tenantId, settingsUpdated: true },
  };
}

/**
 * Returns a safe local-only mock response compatible with completeTenantOnboarding() shape.
 */
export function mockLocalCompleteOnboardingResponse(tenantId: string) {
  return {
    ok: true,
    localDevOnly: true,
    tenant: { id: tenantId, status: 'active' },
  };
}

/**
 * Returns a safe local-only mock response for logo upload.
 * No file is sent to any server.
 */
export function mockLocalLogoUploadResponse(localObjectUrl: string) {
  return {
    ok: true,
    localDevOnly: true,
    // Return the local blob URL as-is for preview; it will not be persisted to draft
    url: localObjectUrl,
  };
}
